import { useState } from 'react';
import { Alert, Button, Field, Input, Select, useConfirm } from '@busbook/ui';
import type { SeatCell } from '@busbook/shared';
import { Page } from '../components/Shell.js';
import { Column, DataTable, Drawer } from '../components/DataTable.js';
import { SeatLayoutEditor, renumber } from '../components/SeatLayoutEditor.js';
import { errorMessage, useResource } from '../lib/useResource.js';
import { api } from '../lib/api.js';
import { useT } from '../i18n/index.js';

interface Layout {
  id: string;
  name: string;
  rows: number;
  columns: number;
  seats: SeatCell[];
  capacity: number;
  busCount: number;
}

interface Bus {
  id: string;
  name: string;
  plateNumber: string;
  seatLayoutId: string;
  active: boolean;
  capacity: number;
  seatLayout: { name: string };
}

interface Tier {
  id: string;
  name: string;
  markupPercent: number;
  isDefault: boolean;
  seatCount: number;
}

export function Fleet() {
  const t = useT();
  const buses = useResource<Bus[]>('/admin/buses');
  const layouts = useResource<Layout[]>('/admin/seat-layouts');
  const tiers = useResource<Tier[]>('/admin/seat-tiers');
  const [busForm, setBusForm] = useState<Partial<Bus> | null>(null);
  const [layoutForm, setLayoutForm] = useState<Partial<Layout> | null>(null);
  const [tierForm, setTierForm] = useState<Partial<Tier> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const { ask, dialog } = useConfirm();

  const busColumns: Column<Bus>[] = [
    { key: 'name', header: t('f.bus'), render: (bus) => <span className="font-medium">{bus.name}</span> },
    { key: 'plate', header: t('f.plate'), render: (bus) => <span className="tabular">{bus.plateNumber}</span> },
    { key: 'layout', header: t('f.layout'), render: (bus) => bus.seatLayout?.name ?? '—' },
    { key: 'capacity', header: t('f.seats'), numeric: true, render: (bus) => bus.capacity },
    {
      key: 'status',
      header: t('f.status'),
      render: (bus) => (
        <span style={{ color: bus.active ? 'var(--signal-departed)' : 'var(--color-faint)' }}>
          {bus.active ? 'In service' : 'Retired'}
        </span>
      ),
    },
  ];

  const layoutColumns: Column<Layout>[] = [
    { key: 'name', header: t('f.layout'), render: (l) => <span className="font-medium">{l.name}</span> },
    { key: 'grid', header: t('fl.grid'), render: (l) => `${l.rows} × ${l.columns}` },
    { key: 'capacity', header: t('f.seats'), numeric: true, render: (l) => l.capacity },
    { key: 'buses', header: t('fl.usedBy'), numeric: true, render: (l) => `${l.busCount} bus${l.busCount === 1 ? '' : 'es'}` },
  ];

  const tierColumns: Column<Tier>[] = [
    { key: 'name', header: t('fl.tierName'), render: (tier) => <span className="font-medium">{tier.name}</span> },
    { key: 'markup', header: t('fl.markup'), numeric: true, render: (tier) => `${tier.markupPercent}%` },
    { key: 'seats', header: t('fl.tierSeatCount'), numeric: true, render: (tier) => tier.seatCount },
    {
      key: 'actions',
      header: '',
      render: (tier) => {
        const hint = tier.isDefault ? t('fl.defaultTierHint') : tier.seatCount > 0 ? t('fl.tierInUseHint') : null;
        return (
          <span className="flex flex-col items-end gap-1">
            <Button
              size="sm"
              variant="ghost"
              disabled={hint !== null}
              onClick={(e) => {
                e.stopPropagation();
                void removeTier(tier);
              }}
            >
              {t('fl.deleteTier')}
            </Button>
            {hint && (
              <span className="text-xs" style={{ color: 'var(--color-faint)' }}>
                {hint}
              </span>
            )}
          </span>
        );
      },
    },
  ];

  async function saveBus() {
    if (!busForm) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: busForm.name ?? '',
        plateNumber: busForm.plateNumber ?? '',
        seatLayoutId: busForm.seatLayoutId ?? '',
        active: busForm.active ?? true,
      };
      if (busForm.id) await api.put(`/admin/buses/${busForm.id}`, payload);
      else await api.post('/admin/buses', payload);
      setBusForm(null);
      await buses.reload();
    } catch (err) {
      setError(errorMessage(err, 'Could not save this bus.'));
    } finally {
      setSaving(false);
    }
  }

  async function saveLayout() {
    if (!layoutForm) return;
    setSaving(true);
    setError(null);
    try {
      const rows = layoutForm.rows ?? 10;
      const columns = layoutForm.columns ?? 4;
      const payload = {
        name: layoutForm.name ?? '',
        rows,
        columns,
        seats: renumber(layoutForm.seats ?? [], rows, columns),
      };
      if (layoutForm.id) await api.put(`/admin/seat-layouts/${layoutForm.id}`, payload);
      else await api.post('/admin/seat-layouts', payload);
      setLayoutForm(null);
      await Promise.all([layouts.reload(), buses.reload()]);
    } catch (err) {
      setError(errorMessage(err, 'Could not save this layout.'));
    } finally {
      setSaving(false);
    }
  }

  async function saveTier() {
    if (!tierForm) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: tierForm.name ?? '',
        markupPercent: tierForm.markupPercent ?? 0,
      };
      if (tierForm.id) await api.put(`/admin/seat-tiers/${tierForm.id}`, payload);
      else await api.post('/admin/seat-tiers', payload);
      setTierForm(null);
      await tiers.reload();
    } catch (err) {
      setError(errorMessage(err, 'Could not save this tier.'));
    } finally {
      setSaving(false);
    }
  }

  async function removeTier(tier: Tier) {
    const yes = await ask({
      title: `Delete “${tier.name}”?`,
      body: 'This cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!yes) return;
    setSaving(true);
    setError(null);
    try {
      await api.delete(`/admin/seat-tiers/${tier.id}`);
      await tiers.reload();
    } catch (err) {
      setError(errorMessage(err, 'Could not delete this tier.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Page
      title={t('nav.fleet')}
      description={t('fl.subtitle')}
      actions={
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setLayoutForm({ rows: 10, columns: 4, seats: [] })}>
            {t('fl.newLayout')}
          </Button>
          <Button variant="secondary" onClick={() => setTierForm({ markupPercent: 0 })}>
            {t('fl.newTier')}
          </Button>
          <Button onClick={() => setBusForm({ active: true })} disabled={!layouts.data?.length}>
            {t('fl.addBus')}
          </Button>
        </div>
      }
    >
      {dialog}
      <section>
        <h2 className="mb-2 text-sm font-semibold">{t('f.buses')}</h2>
        <DataTable
          columns={busColumns}
          rows={buses.data}
          rowKey={(bus) => bus.id}
          loading={buses.loading}
          error={buses.error}
          onRowClick={(bus) => setBusForm(bus)}
          empty={{
            title: t('fl.noBuses'),
            description: layouts.data?.length
              ? 'Add your first bus to start scheduling departures.'
              : 'Create a seat layout first, then add the buses that use it.',
          }}
        />
      </section>

      <section className="mt-9">
        <h2 className="mb-2 text-sm font-semibold">{t('fl.seatLayouts')}</h2>
        <DataTable
          columns={layoutColumns}
          rows={layouts.data}
          rowKey={(layout) => layout.id}
          loading={layouts.loading}
          error={layouts.error}
          onRowClick={(layout) => setLayoutForm(layout)}
          empty={{
            title: t('fl.noLayouts'),
            description: t('fl.layoutHint'),
          }}
        />
      </section>

      <section className="mt-9">
        <h2 className="mb-2 text-sm font-semibold">{t('fl.seatTiers')}</h2>
        <DataTable
          columns={tierColumns}
          rows={tiers.data}
          rowKey={(tier) => tier.id}
          loading={tiers.loading}
          error={tiers.error}
          onRowClick={(tier) => setTierForm(tier)}
        />
      </section>

      <Drawer
        open={busForm !== null}
        title={busForm?.id ? 'Edit bus' : 'Add bus'}
        onClose={() => {
          setBusForm(null);
          setError(null);
        }}
        footer={
          <>
            <Button variant="secondary" onClick={() => setBusForm(null)}>
              {t('f.cancel')}
            </Button>
            <Button onClick={saveBus} loading={saving}>
              {t('fl.saveBus')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {error && <Alert tone="error">{error}</Alert>}
          <Field label={t('f.name')} htmlFor="bus-name" hint={t('fl.nameHint')}>
            <Input
              id="bus-name"
              value={busForm?.name ?? ''}
              onChange={(e) => setBusForm({ ...busForm, name: e.target.value })}
            />
          </Field>
          <Field label={t('fl.plateNumber')} htmlFor="bus-plate">
            <Input
              id="bus-plate"
              value={busForm?.plateNumber ?? ''}
              onChange={(e) => setBusForm({ ...busForm, plateNumber: e.target.value })}
            />
          </Field>
          <Field label={t('fl.seatLayout')} htmlFor="bus-layout">
            <Select
              id="bus-layout"
              value={busForm?.seatLayoutId ?? ''}
              onChange={(e) => setBusForm({ ...busForm, seatLayoutId: e.target.value })}
            >
              <option value="">{t('fl.chooseLayout')}</option>
              {(layouts.data ?? []).map((layout) => (
                <option key={layout.id} value={layout.id}>
                  {layout.name} — {layout.capacity} seats
                </option>
              ))}
            </Select>
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={busForm?.active ?? true}
              onChange={(e) => setBusForm({ ...busForm, active: e.target.checked })}
            />
            {t('fl.inService')}
          </label>
        </div>
      </Drawer>

      <Drawer
        open={layoutForm !== null}
        title={layoutForm?.id ? 'Edit seat layout' : 'New seat layout'}
        onClose={() => {
          setLayoutForm(null);
          setError(null);
        }}
        footer={
          <>
            <Button variant="secondary" onClick={() => setLayoutForm(null)}>
              {t('f.cancel')}
            </Button>
            <Button onClick={saveLayout} loading={saving}>
              {t('fl.saveLayout')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {error && <Alert tone="error">{error}</Alert>}
          <Field label={t('f.name')} htmlFor="layout-name">
            <Input
              id="layout-name"
              value={layoutForm?.name ?? ''}
              onChange={(e) => setLayoutForm({ ...layoutForm, name: e.target.value })}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('f.rows')} htmlFor="layout-rows">
              <Input
                id="layout-rows"
                type="number"
                min={1}
                max={100}
                value={layoutForm?.rows ?? 10}
                onChange={(e) => setLayoutForm({ ...layoutForm, rows: Number(e.target.value) })}
              />
            </Field>
            <Field label={t('f.columns')} htmlFor="layout-cols">
              <Input
                id="layout-cols"
                type="number"
                min={1}
                max={20}
                value={layoutForm?.columns ?? 4}
                onChange={(e) => setLayoutForm({ ...layoutForm, columns: Number(e.target.value) })}
              />
            </Field>
          </div>

          {(layoutForm?.busCount ?? 0) > 0 && (
            <Alert tone="warning">
              This layout is in use. If any upcoming departure uses it, its seats cannot be
              changed — clone it instead.
            </Alert>
          )}

          <SeatLayoutEditor
            rows={layoutForm?.rows ?? 10}
            columns={layoutForm?.columns ?? 4}
            seats={layoutForm?.seats ?? []}
            onChange={(seats) => setLayoutForm({ ...layoutForm, seats })}
            tiers={tiers.data ?? []}
          />
        </div>
      </Drawer>

      <Drawer
        open={tierForm !== null}
        title={tierForm?.id ? 'Edit seat tier' : 'New seat tier'}
        onClose={() => {
          setTierForm(null);
          setError(null);
        }}
        footer={
          <>
            <Button variant="secondary" onClick={() => setTierForm(null)}>
              {t('f.cancel')}
            </Button>
            <Button onClick={saveTier} loading={saving}>
              {t('fl.saveTier')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {error && <Alert tone="error">{error}</Alert>}
          <Field label={t('fl.tierName')} htmlFor="tier-name">
            <Input
              id="tier-name"
              value={tierForm?.name ?? ''}
              onChange={(e) => setTierForm({ ...tierForm, name: e.target.value })}
            />
          </Field>
          <Field label={t('fl.markup')} htmlFor="tier-markup">
            <Input
              id="tier-markup"
              type="number"
              min={0}
              max={500}
              value={tierForm?.markupPercent ?? 0}
              onChange={(e) => setTierForm({ ...tierForm, markupPercent: Number(e.target.value) })}
            />
          </Field>
        </div>
      </Drawer>
    </Page>
  );
}
